/**
 * Phase 136 — StreamX Self-Serve Razorpay Checkout Handler
 * -------------------------------------------------------
 * Wraps the entire "click → pay → activate → celebrate" flow.
 *
 * Three-click promise:
 *   1. Click "Upgrade to Pro"
 *   2. Pay in Razorpay modal (or auto-confirm in mock mode)
 *   3. See "Welcome to StreamX Pro" celebration
 *
 * Backend contract:
 *   GET  /api/livex/me/checkout/config             — { ready, key_id, monthly_paise, yearly_paise, mocked }
 *   POST /api/livex/me/checkout/create-order       — { order: {id, amount}, key_id, mocked }
 *   POST /api/livex/me/checkout/verify-payment     — { subscription, invoice, replays_extended, already_processed, mocked }
 *
 * Mock-mode contract: if `mocked=true`, we skip the Razorpay modal entirely
 * and POST a synthetic success payload to verify-payment so the activation
 * flow can be exercised end-to-end without real Razorpay keys.
 */
import { useState, useCallback } from 'react';
import api from '@/lib/api';
import { Loader2, CreditCard, Sparkles, ShieldCheck } from 'lucide-react';

const RAZORPAY_CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayCheckout() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_CHECKOUT_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SCRIPT;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function LivexCheckoutHandler({
  billingCycle = 'monthly',
  user = null,
  onSuccess,
  onError,
  className = '',
  label = 'Upgrade to StreamX Pro',
  variant = 'primary',           // 'primary' | 'compact'
  testid = 'livex-checkout-upgrade-btn',
}) {
  const [pending, setPending] = useState(false);
  const [stage, setStage] = useState('');  // 'creating' | 'opening' | 'verifying'

  const launch = useCallback(async () => {
    if (pending) return;
    setPending(true);
    setStage('creating');
    try {
      // 1. Create order
      const orderRes = await api.post('/livex/me/checkout/create-order', { billing_cycle: billingCycle });
      const { order, key_id, mocked, fallback_reason } = orderRes.data || {};
      if (!order || !order.id) throw new Error('Order creation failed');

      // 2a. MOCK PATH — skip Razorpay modal, go straight to verify
      if (mocked) {
        setStage('verifying');
        const verifyRes = await api.post('/livex/me/checkout/verify-payment', {
          razorpay_order_id: order.id,
          razorpay_payment_id: `pay_mock_${Math.random().toString(36).slice(2, 16)}`,
          razorpay_signature: '',
          billing_cycle: billingCycle,
        });
        if (onSuccess) onSuccess({ ...verifyRes.data, fallback_reason });
        return;
      }

      // 2b. REAL PATH — open Razorpay modal
      setStage('opening');
      const ok = await loadRazorpayCheckout();
      if (!ok || !window.Razorpay) {
        throw new Error('Razorpay checkout script failed to load. Check your connection and try again.');
      }

      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.id,
        name: 'StreamX by Out-Play',
        description: `StreamX Pro · ${billingCycle === 'yearly' ? 'Annual' : 'Monthly'} subscription`,
        image: '',
        prefill: {
          email: user?.email || '',
          name: user?.full_name || user?.name || '',
          contact: user?.phone || '',
        },
        notes: { kind: 'livex_subscription', billing_cycle: billingCycle },
        theme: { color: '#FF5A1F' },
        modal: {
          ondismiss: () => {
            setPending(false);
            setStage('');
          },
        },
        handler: async (response) => {
          try {
            setStage('verifying');
            const verifyRes = await api.post('/livex/me/checkout/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              billing_cycle: billingCycle,
            });
            if (onSuccess) onSuccess(verifyRes.data);
          } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Payment verification failed';
            if (onError) onError(msg);
            setPending(false);
            setStage('');
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        if (onError) onError(resp?.error?.description || 'Payment failed');
        setPending(false);
        setStage('');
      });
      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Checkout failed';
      if (onError) onError(msg);
    } finally {
      if (stage === 'verifying') setPending(false);
    }
  }, [billingCycle, user, onSuccess, onError, pending, stage]);

  const stageLabel = {
    creating: 'Preparing checkout…',
    opening: 'Opening Razorpay…',
    verifying: 'Activating Pro…',
  }[stage];

  if (variant === 'compact') {
    return (
      <button
        onClick={launch}
        disabled={pending}
        data-testid={testid}
        className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white rounded-md border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
        style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
      >
        {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
        <span>{pending ? stageLabel || 'Working…' : label}</span>
      </button>
    );
  }

  return (
    <button
      onClick={launch}
      disabled={pending}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', boxShadow: '0 8px 24px rgba(255,90,31,0.30)' }}
    >
      {pending ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {stageLabel || 'Working…'}
        </>
      ) : (
        <>
          <CreditCard className="h-4 w-4" />
          {label}
          <ShieldCheck className="h-3.5 w-3.5 opacity-70" />
        </>
      )}
    </button>
  );
}
